import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, PasswordInput, TextInput } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_ADMIN_COMPANY,
  UPDATE_ADMIN_COMPANY,
} from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import { isAdminOrOps } from 'src/lib/auth/permissions'

import FieldLabel from '../../companyTab/components/FieldLabel'

import DriverIQUdfFieldsSection from './DriverIQUdfFieldsSection'
import { useDriverIQUdfFieldsEditor } from './useDriverIQUdfFieldsEditor'

interface BackgroundSettingsCardProps {
  companyData: {
    companyId: string
    companyHireRightName: string | null
    hireRightCompanyLogin: string | null
    hireRightSubAccountCompanyLogin: string | null
    asurintUsername: string | null
    asurintPassword: string | null
    asurintAccountId: string | null
    asurintLocationId: string | null
    driverIQAccountCode: string | null
    driverIQTenant: string | null
    driverIQClientId: string | null
    driverIQClientSecret: string | null
  }
  initialOpened?: boolean
}

const BackgroundSettingsCard = ({
  companyData,
  initialOpened = false,
}: BackgroundSettingsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [updateCompany, { loading: savingCompany }] = useMutation(
    UPDATE_ADMIN_COMPANY,
    {
      refetchQueries: [
        {
          query: GET_ADMIN_COMPANY,
          variables: { id: values.companyId },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  // DriverIQ order fields (UDF1–UDF3) share this card's Edit / Save / Cancel
  const udfEditor = useDriverIQUdfFieldsEditor({ editing })
  const showUdfSection = Boolean(values.driverIQAccountCode)
  const loading = savingCompany || udfEditor.loading

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    udfEditor.reset()
    setEditing(false)
  }

  const handleSave = async () => {
    if (showUdfSection && !udfEditor.validate()) {
      toast('Fix the DriverIQ order fields before saving', 'error')
      return
    }

    const { companyId, ...rest } = values

    try {
      await Promise.all([
        updateCompany({
          variables: {
            id: companyId,
            input: rest,
          },
        }),
        showUdfSection ? udfEditor.save() : Promise.resolve(),
      ])
      toast('Background check settings updated successfully', 'success')
      setEditing(false)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Please try again.'
      toast(`Failed to update settings: ${message}`, 'error')
    }
  }

  const hireRightCompanyNameLabel = (
    <FieldLabel
      label="HireRight Company Name"
      tooltip={
        <>
          The account name provided by HireRight for your company.
          <br />
          <br />
          This name is used to identify your company in the HireRight system and
          is required for background check integrations.
        </>
      }
    />
  )

  const hireRightCompanyLoginLabel = (
    <FieldLabel
      label="HireRight Company Login"
      tooltip={
        <>
          The account code provided by HireRight for your company.
          <br />
          <br />
          This login code is used to authenticate and connect with
          HireRight&apos;s background check services.
        </>
      }
    />
  )

  const hireRightSubAccountLoginLabel = (
    <FieldLabel
      label="HireRight SubAccount Login"
      tooltip={
        <>
          The subaccount login code provided by HireRight, if applicable.
          <br />
          <br />
          If no subaccount is configured, enter the same value as the Company
          Login. This allows for additional account organization within
          HireRight.
        </>
      }
    />
  )

  const asurintUsernameLabel = (
    <FieldLabel
      label="Asurint Username"
      tooltip={
        <>
          The username provided by Asurint for your company account.
          <br />
          <br />
          This username is used to authenticate and connect with Asurint&apos;s
          background check services.
        </>
      }
    />
  )

  const asurintPasswordLabel = (
    <FieldLabel
      label="Asurint Password"
      tooltip={
        <>
          The password provided by Asurint for your company account.
          <br />
          <br />
          This password is used along with the username to authenticate and
          connect with Asurint&apos;s background check services.
        </>
      }
    />
  )

  const asurintAccountIdLabel = (
    <FieldLabel
      label="Asurint Account ID"
      tooltip={
        <>
          The account ID provided by Asurint for your company.
          <br />
          <br />
          This account ID is used to identify your company account in the
          Asurint system and is required for background check integrations.
        </>
      }
    />
  )

  const asurintLocationIdLabel = (
    <FieldLabel
      label="Asurint Location ID"
      tooltip={
        <>
          The location ID provided by Asurint for your company.
          <br />
          <br />
          This location ID is used to specify the location associated with your
          company account in the Asurint system.
        </>
      }
    />
  )

  const driverIQAccountCodeLabel = (
    <FieldLabel
      label="DriverIQ Account ID"
      tooltip={
        <>
          The account code provided by DriverIQ (Cisive) for your company (e.g.,
          VERQ02).
          <br />
          <br />
          This code identifies your company when ordering background checks
          through DriverIQ.
        </>
      }
    />
  )

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Background Check Settings
        {isAdmin && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            disabled={showUdfSection && !udfEditor.fieldsLoaded}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {isAdmin && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          {/* First row: Asurint fields */}
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Asurint Username */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={asurintUsernameLabel}
                  value={values.asurintUsername ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      asurintUsername: e.target.value,
                    })
                  }
                  placeholder="Enter Asurint username"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={asurintUsernameLabel}
                  value={values.asurintUsername || '-'}
                />
              )}
            </div>

            {/* Asurint Password */}
            <div className="col-span-3">
              {editing ? (
                <PasswordInput
                  label={asurintPasswordLabel}
                  value={values.asurintPassword ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      asurintPassword: e.target.value,
                    })
                  }
                  placeholder="Enter Asurint password"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={asurintPasswordLabel}
                  value={values.asurintPassword ? '••••••••' : '-'}
                />
              )}
            </div>

            {/* Asurint Account ID */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={asurintAccountIdLabel}
                  value={values.asurintAccountId ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      asurintAccountId: e.target.value,
                    })
                  }
                  placeholder="Enter Asurint account ID"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={asurintAccountIdLabel}
                  value={values.asurintAccountId || '-'}
                />
              )}
            </div>

            {/* Asurint Location ID */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={asurintLocationIdLabel}
                  value={values.asurintLocationId ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      asurintLocationId: e.target.value,
                    })
                  }
                  placeholder="Enter Asurint location ID"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={asurintLocationIdLabel}
                  value={values.asurintLocationId || '-'}
                />
              )}
            </div>
          </div>

          {/* Second row: HireRight fields */}
          <div className={`grid grid-cols-12 gap-3`}>
            {/* HireRight Company Name */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={hireRightCompanyNameLabel}
                  value={values.companyHireRightName ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      companyHireRightName: e.target.value,
                    })
                  }
                  placeholder="Enter HireRight company name"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={hireRightCompanyNameLabel}
                  value={values.companyHireRightName || '-'}
                />
              )}
            </div>

            {/* HireRight Company Login */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={hireRightCompanyLoginLabel}
                  value={values.hireRightCompanyLogin ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      hireRightCompanyLogin: e.target.value,
                    })
                  }
                  placeholder="Enter HireRight company login"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={hireRightCompanyLoginLabel}
                  value={values.hireRightCompanyLogin || '-'}
                />
              )}
            </div>

            {/* HireRight SubAccount Login */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={hireRightSubAccountLoginLabel}
                  value={values.hireRightSubAccountCompanyLogin ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      hireRightSubAccountCompanyLogin: e.target.value,
                    })
                  }
                  placeholder="Enter HireRight subaccount login"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={hireRightSubAccountLoginLabel}
                  value={values.hireRightSubAccountCompanyLogin || '-'}
                />
              )}
            </div>
          </div>

          {/* Third row: DriverIQ Production fields */}
          <div className={`grid grid-cols-12 gap-3`}>
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={driverIQAccountCodeLabel}
                  value={values.driverIQAccountCode ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      driverIQAccountCode: e.target.value,
                    })
                  }
                  placeholder="e.g., DNIQ01"
                  maxLength={10}
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={driverIQAccountCodeLabel}
                  value={values.driverIQAccountCode || '-'}
                />
              )}
            </div>
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label="DriverIQ Tenant"
                  value={values.driverIQTenant ?? ''}
                  onChange={(e) =>
                    setValues({ ...values, driverIQTenant: e.target.value })
                  }
                  placeholder="Azure AD Tenant ID"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label="DriverIQ Tenant"
                  value={values.driverIQTenant || '-'}
                />
              )}
            </div>
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label="DriverIQ Client ID"
                  value={values.driverIQClientId ?? ''}
                  onChange={(e) =>
                    setValues({ ...values, driverIQClientId: e.target.value })
                  }
                  placeholder="OAuth Client ID"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label="DriverIQ Client ID"
                  value={values.driverIQClientId || '-'}
                />
              )}
            </div>
            <div className="col-span-3">
              {editing ? (
                <PasswordInput
                  label="DriverIQ Client Secret"
                  value={values.driverIQClientSecret ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      driverIQClientSecret: e.target.value,
                    })
                  }
                  placeholder="OAuth Client Secret"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label="DriverIQ Client Secret"
                  value={values.driverIQClientSecret ? '••••••••' : '-'}
                />
              )}
            </div>
          </div>

          {/* DriverIQ order fields (UDF1–UDF3), secondary to the credentials */}
          {showUdfSection && (
            <DriverIQUdfFieldsSection editor={udfEditor} editing={editing} />
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default BackgroundSettingsCard
